import React from 'react';

import { DialogFooter, Progress, Typography } from '@crossnokaye/ux-common';
import { Button } from '@crossnokaye/ux-common/ckds';

import { TooltipButton } from '@/components/molecules/tooltip-button';

import { SaveProgressSummary } from './save-progress-summary';
import type { PreviewSaveStatus } from './setting-preview.types';

interface SettingsSaveFooterProps {
  hasPendingSave: boolean;
  isCancelled: boolean;
  isSubmitting: boolean;
  saveComplete: boolean;
  allSucceeded: boolean;
  changeCount: number;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
  progressPercent: number;
  disableSave: boolean;
  onCancelPending: () => void;
  onClose: () => void;
  onConfirm: () => void;
}

const getDisabledSaveTooltip = (
  disableSave: boolean,
  changeCount: number,
  isSubmitting: boolean
): string | undefined => {
  if (disableSave) return 'One or more changes have validation errors';
  if (changeCount === 0) return 'No pending changes to save';
  if (isSubmitting) return 'Save is already in progress';
  return undefined;
};

export const SettingsSaveFooter = ({
  hasPendingSave,
  isCancelled,
  isSubmitting,
  saveComplete,
  allSucceeded,
  changeCount,
  saveProgressByChangeId,
  progressPercent,
  disableSave,
  onCancelPending,
  onClose,
  onConfirm,
}: SettingsSaveFooterProps): React.ReactElement => {
  const hasSaveProgress = Object.keys(saveProgressByChangeId).length > 0;

  const progressSection = hasSaveProgress ? (
    <div className="space-y-4 w-full">
      {hasSaveProgress && <Progress value={progressPercent} className="h-1.5" />}
      <SaveProgressSummary progressMap={saveProgressByChangeId} />
    </div>
  ) : null;

  if (hasPendingSave) {
    return (
      <div className="flex-shrink-0 flex flex-col gap-3 p-stack-sm sm:p-none">
        {progressSection}
        <DialogFooter className="flex gap-2 flex-row justify-center sm:justify-end p-0">
          <Typography
            variant="body"
            className="text-xs text-ckds-text-fg-tier3 mr-auto self-center"
          >
            Cancelling will only stop queued items. Items already submitted will finish.
          </Typography>
          <Button
            type="button"
            variant="tier3"
            disabled={isCancelled}
            onClick={onCancelPending}
            className="flex-1 sm:flex-none"
          >
            Cancel queued
          </Button>
          <Button
            type="button"
            variant="brand"
            disabled={isCancelled || isSubmitting}
            className="flex-1 sm:flex-none"
          >
            Saving...
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 flex flex-col gap-3 p-stack-sm sm:p-none">
      {progressSection}
      <DialogFooter className="flex gap-2 flex-row justify-center sm:justify-end p-0">
        <Button
          type="button"
          variant={allSucceeded || saveComplete ? 'brand' : 'tier3'}
          className="sm:w-36 flex-1 sm:flex-none"
          onClick={onClose}
        >
          Close
        </Button>
        {!saveComplete && (
          <TooltipButton
            type="button"
            variant="brand"
            disabled={isSubmitting || changeCount === 0 || disableSave}
            onClick={onConfirm}
            className="flex-1 sm:flex-none"
            tooltipContent={getDisabledSaveTooltip(disableSave, changeCount, isSubmitting)}
            delayDuration={0}
          >
            {`SAVE ${changeCount} CHANGE${changeCount !== 1 ? 'S' : ''}`}
          </TooltipButton>
        )}
      </DialogFooter>
    </div>
  );
};
